import React, { useState } from "react";
import { FaSignOutAlt } from "react-icons/fa";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import useAuthUser from "../../components/common/useAuthUser";
import { Link, useNavigate } from "react-router-dom";

const Profile = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { authUser, isLoading } = useAuthUser();

  const token = Cookies.get("gameport");
  const API_URL = process.env.REACT_APP_API_URL;

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
  });

  const { data: myOrder, isLoading: myOrderLoading } = useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/order/my-orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch orders");
      return data;
    },
    enabled: !!token,
  });

  const { mutate: logout, isPending: loggingOut } = useMutation({
    mutationKey: ["logout"],
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/auth/signout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Logout failed");
      return data;
    },
    onSuccess: () => {
      Cookies.remove("gameport");
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      toast.success("Logged out");
      navigate("/signin");
    },
    onError: (err) => {
      toast.error(err.message || "Logout failed");
    },
  });

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSave = () => {
    setEditing(false);
    toast.success("Profile updated!");
  };

  if (isLoading || !authUser) return <p>Loading...</p>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* User Info */}
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="flex-1 space-y-2">
          {editing ? (
            <div className="space-y-3">
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full border-b-2 border-gray-300 px-2 py-1 focus:outline-none focus:border-black font-semibold"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border-b-2 border-gray-300 px-2 py-1 focus:outline-none focus:border-black font-semibold"
              />
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-black text-white rounded-lg hover:opacity-90 font-semibold"
              >
                Save
              </button>
            </div>
          ) : (
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {authUser?.fullName}
              </h2>
              <p className="text-gray-700">{authUser?.email}</p>
            </div>
          )}
        </div>
      </div>

      {/* Orders Summary */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-900">Recent Orders</h3>

        {myOrderLoading ? (
          <p className="text-gray-500">Loading orders...</p>
        ) : myOrder?.data?.length > 0 ? (
          <div className="space-y-2">
            {myOrder?.data?.slice(0, 5)?.map((order) => (
              <Link
                to={`/order/${order?._id}`}
                key={order?._id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-gray-200"
              >
                <div>
                  <p className="font-semibold">Order - #{order?.orderCode}</p>
                  <p className="text-gray-500 text-sm">
                    {order?.items?.length} items
                  </p>
                </div>

                <div className="text-gray-900 font-bold">
                  ₦{order?.total?.toLocaleString()}
                </div>

                <div className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-800 w-fit">
                  {order?.orderStatus?.toUpperCase()}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No orders yet.</p>
        )}
      </div>
        <div>
          <Link
            to="/reset-password"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            Reset Password
          </Link>
        </div>
      {/* Logout */}
      <button
        onClick={() => logout()}
        disabled={loggingOut}
        className={`flex items-center gap-2 px-4 py-2 border rounded-lg font-semibold transition-all
          ${
            loggingOut
              ? "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed"
              : "border-red text-red hover:bg-red hover:text-white"
          }`}
      >
        <FaSignOutAlt /> {loggingOut ? "Logging out..." : "Logout"}
      </button>
    </div>
  );
};

export default Profile;
